import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { useEffect, useMemo, useState } from 'react';

import BankAccountStatus from '@/enums/bankAccountStatus';
import { usePrevious } from '@/hooks/usePrevious';

import { BankAccountOnFileWithPlaidVerifyContainer } from '../Plaid/BankAccountOnFileWithPlaidVerifyContainer';

type Props = {
    bankAccounts: BankAccount[];
    deleteBankAccount?: (bankAccountId: number) => void;
    selectBankAccount: (bankAccount: BankAccount) => void;
};

const BankAccountSelectionComponent = ({ bankAccounts, deleteBankAccount, selectBankAccount }: Props) => {
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>();
    const previousNumberOfBankAccounts = usePrevious(bankAccounts?.length);

    useEffect(() => {
        if (
            Boolean(bankAccounts) &&
            (!selectedPaymentMethodId || previousNumberOfBankAccounts !== bankAccounts?.length)
        ) {
            const defaultBankAccount = bankAccounts.find((bankAccount) => bankAccount.defaultAccount);
            setSelectedPaymentMethodId(`bankAccount-${defaultBankAccount?.bankAccountId}`);
            if (defaultBankAccount && selectBankAccount) {
                selectBankAccount(defaultBankAccount);
            }
        }
    }, [bankAccounts, previousNumberOfBankAccounts, selectBankAccount, selectedPaymentMethodId]);

    const bankAccountRadios = useMemo(() => {
        if (!bankAccounts) {
            return null;
        }
        return bankAccounts.map((bankAccount) => {
            const radioId = `bankAccount-${bankAccount.bankAccountId}`;
            const selected = selectedPaymentMethodId === radioId;
            const disabled =
                bankAccount.status === BankAccountStatus.Pending ||
                bankAccount.status === BankAccountStatus.PendingManual ||
                bankAccount.status === BankAccountStatus.PendingAutomatic;
            return (
                <Radio.Option
                    checked={selected}
                    className="mb-md w-full items-start pl-md [&_input]:mt-3xs"
                    disabled={disabled}
                    key={bankAccount.bankAccountId}
                    name="bankAccount"
                    onChange={() => {
                        setSelectedPaymentMethodId(radioId);
                        selectBankAccount(bankAccount);
                    }}
                    value={radioId}
                >
                    <div>
                        <BankAccountOnFileWithPlaidVerifyContainer
                            bankAccount={bankAccount}
                            deleteAccount={
                                deleteBankAccount
                                    ? (event) => {
                                          event.stopPropagation();
                                          if (selected) {
                                              setSelectedPaymentMethodId(undefined);
                                          }
                                          deleteBankAccount(bankAccount.bankAccountId);
                                      }
                                    : undefined
                            }
                            deleteAccountLinkText="Remove"
                        />
                    </div>
                </Radio.Option>
            );
        });
    }, [bankAccounts, deleteBankAccount, selectBankAccount, selectedPaymentMethodId]);

    return <div>{bankAccountRadios}</div>;
};

export const BankAccountSelection = BankAccountSelectionComponent;
